import React, { useState, useRef, useEffect } from "react";
import "./CarInsurance.css";
import { Link } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { useDispatch, useSelector } from "react-redux";
import {
  submitEnquiryForm,
  resetEnquiryFormState,
} from "../../store/slices/enquiryFormSlice";
import { toast } from "react-toastify";

function CarInsurance() {
  const [activeTab, setActiveTab] = useState("inclusions");
  const [formData, setFormData] = useState({
    fullname: "",
    phone: "",
    email: "",
    referral_name: "",
    referral_email: "",
    message: "",
  });

  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitStatus, setSubmitStatus] = useState("");
  const [recaptchaToken, setCaptchaValue] = useState(null);
  const [errors, setErrors] = useState({});
  const recaptchaRef = useRef(null);

  // Create a ref for the form section
  const formSectionRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRecaptchaChange = (value) => {
    setCaptchaValue(value);

    if (errors.captcha) {
      setErrors((prev) => ({
        ...prev,
        captcha: "",
      }));
    }
  };
  // Function to scroll to form
  const scrollToForm = () => {
    formSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  // Format backend errors
  const formatBackendErrors = (errors) => {
    if (!errors || typeof errors !== "object")
      return "An error occurred. Please try again.";

    return Object.entries(errors)
      .flatMap(([field, messages]) =>
        messages.map(
          (m) =>
            `${field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}: ${m}`,
        ),
      )
      .join("\n");
  };

  // Form validation
  const validateForm = () => {
    const phoneRegex = /^[0-9]{10}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Required fields
    if (!formData.fullname || !formData.email || !formData.phone)
      return "Please fill in all required fields";

    // Phone validation
    if (!phoneRegex.test(formData.phone))
      return "Please enter a valid 10-digit mobile number";

    // Email validation
    if (!emailRegex.test(formData.email))
      return "Please enter a valid email address";

    // Referral email validation
    if (formData.referral_email && !emailRegex.test(formData.referral_email))
      return "Please enter a valid referral email address";

    // Referral email should not match main email
    if (
      formData.referral_email &&
      formData.email.toLowerCase() === formData.referral_email.toLowerCase()
    ) {
      return "Referral email cannot be the same as your email";
    }

    // Terms check
    if (!acceptTerms) return "Please accept the terms and conditions";

    // reCAPTCHA check
    if (!recaptchaToken) return "Please complete the reCAPTCHA verification";

    return null;
  };

  // Clear message after timeout
  const clearMessageAfterTimeout = () => {
    setTimeout(() => {
      setSubmitMessage("");
      setSubmitStatus("");
    }, 5000);
  };

  // Handle form submit
  const dispatch = useDispatch();
  const { success, error, message } = useSelector((state) => state.enquiryForm);

  const handleSubmit = (e) => {
    e.preventDefault();

    const errorMsg = validateForm();
    if (errorMsg) {
      setSubmitMessage(errorMsg);
      setSubmitStatus("error");
      clearMessageAfterTimeout();
      return;
    }
    if (!recaptchaToken) {
      toast.error("Please verify that you are human");
      return;
    }
    setIsSubmitting(true);
    setSubmitMessage("");

    const payload = {
      fullname: formData.fullname.trim(),
      email: formData.email.trim().toLowerCase(),
      mobile: formData.phone.trim(),

      require_service: "Car Insurance",
      page_url: window.location.href,
      recaptcha_token: recaptchaToken,
      // Optional fields
      ...(formData.referral_name && {
        reference_name: formData.referral_name.trim(),
      }),
      ...(formData.referral_email && {
        reference_email: formData.referral_email.trim().toLowerCase(),
      }),
      ...(formData.message && {
        additional_data: JSON.stringify({
          message: formData.message.trim(),
        }),
      }),
    };

    dispatch(submitEnquiryForm(payload));
  };
  useEffect(() => {
    if (success) {
      setSubmitMessage("Form submitted successfully ✅");
      setSubmitStatus("success");

      setFormData({
        fullname: "",
        phone: "",
        email: "",
        referral_name: "",
        referral_email: "",
        message: "",
      });

      setAcceptTerms(false);

      setCaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }

      clearMessageAfterTimeout();
      setIsSubmitting(false);

      dispatch(resetEnquiryFormState());
    }
  }, [success, message]); // ✅ add message
  useEffect(() => {
    if (error) {
      setSubmitMessage(error);
      setSubmitStatus("error");

      clearMessageAfterTimeout();
      setIsSubmitting(false);
    }
  }, [error]);

  return (
    <div className="car-insurance-wrapper">
      {/* PREMIUM HERO SECTION WITH 3D EFFECT */}
      <section className="ci-premium-hero">
        <div className="ci-container">
          <div className="ci-premium-grid">
            <div className="ci-premium-content">
              <div className="ci-premium-tagline">
                <span className="ci-premium-badge">
                  🚗 Trusted by 2M+ Drivers
                </span>
              </div>
              <h1 className="ci-premium-title">
                <span className="ci-premium-title-main">Drive</span>
                <span className="ci-premium-title-accent">Protected</span>
              </h1>
              <p className="ci-premium-subtitle">
                Car Insurance starting at just ₹2,094/year*
              </p>

              <div className="ci-premium-features">
                <div className="ci-premium-feature">
                  <div className="ci-premium-feature-icon">🔧</div>
                  <div>
                    <h4>Doorstep Repairs</h4>
                    <p>Cashless service at your location</p>
                  </div>
                </div>
                <div className="ci-premium-feature">
                  <div className="ci-premium-feature-icon">🛡️</div>
                  <div>
                    <h4>₹15 Lakh Cover</h4>
                    <p>Personal accident coverage</p>
                  </div>
                </div>
                <div className="ci-premium-feature">
                  <div className="ci-premium-feature-icon">🏷️</div>
                  <div>
                    <h4>50% NCB</h4>
                    <p>No Claim Bonus benefits</p>
                  </div>
                </div>
              </div>

              <div className="ci-premium-stats">
                <div className="ci-premium-stat">
                  <span className="ci-premium-stat-value">5,000+</span>
                  <span className="ci-premium-stat-label">
                    Cashless Garages
                  </span>
                </div>
                <div className="ci-premium-stat">
                  <span className="ci-premium-stat-value">24/7</span>
                  <span className="ci-premium-stat-label">Claim Support</span>
                </div>
                <div className="ci-premium-stat">
                  <span className="ci-premium-stat-value">98%</span>
                  <span className="ci-premium-stat-label">
                    Claim Settlement
                  </span>
                </div>
              </div>
            </div>

            <div className="ci-premium-visual">
              <div className="ci-premium-car-wrapper">
                <div className="ci-premium-car-image">
                  <img
                    src="https://www.icicilombard.com/docs/default-source/apps/fwfresh/fw-landing/images/widget-men-vector.png"
                    alt="Car Insurance"
                  />
                </div>
                {/* <div className="ci-premium-shield">
                  <span>🛡️</span>
                </div> */}
                {/* <div className="ci-premium-bubble bubble-1">⚡ Instant Claim</div>
                <div className="ci-premium-bubble bubble-2">🔧 Free Inspection</div>
                <div className="ci-premium-bubble bubble-3">💰 Save 50%</div> */}
              </div>
            </div>
          </div>

          {/* MODERN SPLIT FORM WITH GLASSMORPHISM - Added ref here */}
          <div className="ci-modern-form-section" ref={formSectionRef}>
            <div className="ci-modern-form-container">
              <div className="ci-modern-form-left">
                <div className="ci-modern-form-left-content">
                  <span className="ci-modern-form-badge">
                    Why Insure With Us?
                  </span>
                  <h3>Complete Protection For Your Vehicle</h3>
                  <div className="ci-modern-form-points">
                    <div className="ci-modern-form-point">
                      <span>✓</span> Zero Depreciation Cover
                    </div>
                    <div className="ci-modern-form-point">
                      <span>✓</span> Roadside Assistance 24/7
                    </div>
                    <div className="ci-modern-form-point">
                      <span>✓</span> Cashless Repairs
                    </div>
                    <div className="ci-modern-form-point">
                      <span>✓</span> NCB Protection
                    </div>
                    <div className="ci-modern-form-point">
                      <span>✓</span> Engine Protect Plus
                    </div>
                    <div className="ci-modern-form-point">
                      <span>✓</span> Consumables Cover
                    </div>
                  </div>
                  <div className="ci-modern-form-rating">
                    <div className="ci-modern-form-stars">★★★★★</div>
                    <p>Trusted by 1 Million+ Customers</p>
                  </div>
                </div>
              </div>

              <div className="ci-modern-form-right">
                <div className="ci-modern-form-card">
                  <div className="ci-modern-form-header">
                    <h3>Get Instant Quote</h3>
                    <p>Compare 25+ insurers in 2 minutes</p>
                  </div>

                  {submitMessage && (
                    <div className={`ci-modern-message ${submitStatus}`}>
                      <span className="ci-modern-message-icon">
                        {submitStatus === "success" ? "✓" : "⚠️"}
                      </span>
                      <span>{submitMessage}</span>
                      <button
                        className="ci-modern-message-close"
                        onClick={() => {
                          setSubmitMessage("");
                          setSubmitStatus("");
                        }}
                      >
                        ×
                      </button>
                    </div>
                  )}

                  <form className="ci-modern-form" onSubmit={handleSubmit}>
                    <div className="ci-modern-input-group">
                      <input
                        type="text"
                        name="fullname"
                        value={formData.fullname}
                        onChange={handleInputChange}
                        placeholder="Full Name *"
                        required
                      />
                    </div>

                    <div className="ci-modern-row">
                      <div className="ci-modern-input-group">
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="9737304440 *"
                          required
                          pattern="[0-9]{10}"
                        />
                      </div>
                      <div className="ci-modern-input-group">
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="loansviakrishna@gmail.com *
"
                          required
                        />
                      </div>
                    </div>

                    <div className="ci-modern-row">
                      <div className="ci-modern-input-group">
                        <input
                          type="text"
                          name="referral_name"
                          value={formData.referral_name}
                          onChange={handleInputChange}
                          placeholder="Referral Name (Optional)"
                        />
                      </div>
                      <div className="ci-modern-input-group">
                        <input
                          type="email"
                          name="referral_email"
                          value={formData.referral_email}
                          onChange={handleInputChange}
                          placeholder="loansviakrishna@gmail.com *
"
                        />
                      </div>
                    </div>

                    <div className="ci-modern-input-group">
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Tell us about your Car Insurance needs (Optional)"
                        rows="2"
                      />
                    </div>

                    <div className="li-recaptcha">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey="6Ldk_IksAAAAAO8Aq02xgOPeBlszLT2K30hdJCjM"
                        onChange={handleRecaptchaChange}
                        // ✅ ADD THIS (important)
                        onExpired={() => {
                          setCaptchaValue(null);
                          setErrors((prev) => ({
                            ...prev,
                            captcha: "Captcha expired, please verify again",
                          }));
                        }}
                        // ✅ OPTIONAL (network issue handle)
                        onErrored={() => {
                          setCaptchaValue(null);
                          setErrors((prev) => ({
                            ...prev,
                            captcha: "Captcha failed to load. Try again.",
                          }));
                        }}
                      />
                    </div>

                    <div className="ci-modern-terms">
                      <label>
                        <input
                          type="checkbox"
                          checked={acceptTerms}
                          onChange={(e) => setAcceptTerms(e.target.checked)}
                        />
                        <span>
                          I agree to receive loan updates and promotional from{" "}
                          <strong>Krishana Finconcept</strong> via WhatsApp and
                          other accept the{" "}
                          <a
                            href="/privacy-security-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Terms & Conditions
                          </a>
                        </span>
                      </label>
                    </div>

                    <button
                      type="submit"
                      className="ci-modern-submit"
                      disabled={!acceptTerms || isSubmitting || !recaptchaToken}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="ci-modern-spinner"></span>
                          Submitting...
                        </>
                      ) : (
                        <>
                          Get Free Quote
                          <span>→</span>
                        </>
                      )}
                    </button>

                    <p className="ci-modern-footer">
                      No spam. 100% privacy guaranteed
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3D BENEFIT CARDS SECTION */}
      <section className="ci-3d-benefits">
        <div className="ci-container">
          <div className="ci-section-header-center">
            <span className="ci-section-mini-badge">Why Choose Us</span>
            <h2 className="ci-section-main-title">
              The <span className="ci-gradient-text">Ultimate</span> Protection
              For Your Car
            </h2>
            <p className="ci-section-description">
              Experience peace of mind with our comprehensive coverage
            </p>
          </div>

          <div className="ci-3d-card-grid">
            <div className="ci-3d-card">
              <div className="ci-3d-card-inner">
                <div className="ci-3d-card-front">
                  <div className="ci-3d-icon">🔧</div>
                  <h3>5,000+ Cashless Garages</h3>
                </div>
                <div className="ci-3d-card-back">
                  <p>
                    Get your vehicle repaired at thousands of network garages
                    across India without paying upfront.
                  </p>
                  <span className="ci-3d-badge">PAN India Network</span>
                </div>
              </div>
            </div>

            <div className="ci-3d-card ci-3d-card-featured">
              <div className="ci-3d-card-inner">
                <div className="ci-3d-card-front">
                  <div className="ci-3d-icon">⚡</div>
                  <h3>Fast Claim Settlement</h3>
                </div>
                <div className="ci-3d-card-back">
                  <p>
                    Easy claim process with quick approvals and minimal
                    documentation. Get your claim in 24 hours.
                  </p>
                  <span className="ci-3d-badge">&lt; 24 Hours</span>
                </div>
              </div>
            </div>

            <div className="ci-3d-card">
              <div className="ci-3d-card-inner">
                <div className="ci-3d-card-front">
                  <div className="ci-3d-icon">💰</div>
                  <h3>Affordable Premium</h3>
                </div>
                <div className="ci-3d-card-back">
                  <p>
                    Compare multiple insurers and get the best premium with
                    maximum coverage. Save up to 40%.
                  </p>
                  <span className="ci-3d-badge">Save up to 40%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SPLIT SCREEN ABOUT SECTION */}
      <section className="ci-split-about">
        <div className="ci-split-container">
          <div className="ci-split-left">
            <div className="ci-split-image-wrapper">
              <img
                src="images/insurance/whatcar2.png"
                // src="https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&w=800&q=80"
                alt="Car Insurance"
              />
              <div className="ci-split-image-badge">
                <span>Trusted by 1M+</span>
              </div>
            </div>
          </div>
          <div className="ci-split-right">
            <span className="ci-split-tag">Know More</span>
            <h2>What is Car Insurance?</h2>
            <p className="ci-split-text">
              Car insurance is a contract between the vehicle owner and the
              insurance company that protects the car owner from financial
              losses caused by accidents, theft, fire or disasters.
            </p>
            <p className="ci-split-text">
              It also covers third-party liabilities where the insured vehicle
              damages another vehicle, property or causes injury.
            </p>

            <div className="ci-split-features">
              <div className="ci-split-feature">
                <span>✓</span> Protection against accidents
              </div>
              <div className="ci-split-feature">
                <span>✓</span> Coverage for natural disasters
              </div>
              <div className="ci-split-feature">
                <span>✓</span> Third-party liability protection
              </div>
              <div className="ci-split-feature">
                <span>✓</span> Peace of mind for vehicle owners
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MODERN COMPARISON TABLE */}
      <section className="ci-modern-comparison">
        <div className="ci-container">
          <div className="ci-section-header-center">
            <span className="ci-section-mini-badge">Compare Policies</span>
            <h2 className="ci-section-main-title">
              Choose The Right{" "}
              <span className="ci-gradient-text">Coverage</span>
            </h2>
            <p className="ci-section-description">
              Understanding different car insurance policies
            </p>
          </div>

          <div className="ci-modern-table-wrapper">
            <table className="ci-modern-table">
              <thead>
                <tr>
                  <th>Parameters</th>
                  <th>Comprehensive</th>
                  <th>Own Damage</th>
                  <th>Third Party</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="ci-table-param">Coverages offered</td>
                  <td>
                    Covers both damages to your own car and third-party
                    liabilities
                  </td>
                  <td>
                    Covers damages caused to your own car due to accidents,
                    man-made or natural disasters
                  </td>
                  <td>
                    Covers only third-party liabilities like damage to other
                    vehicle, property or person
                  </td>
                </tr>
                <tr>
                  <td className="ci-table-param">Own Damage Cover</td>
                  <td>
                    <span className="ci-check">✓</span> Included
                  </td>
                  <td>
                    <span className="ci-check">✓</span> Included
                  </td>
                  <td>
                    <span className="ci-cross">✖</span> Not Included
                  </td>
                </tr>
                <tr>
                  <td className="ci-table-param">Third-Party Liability</td>
                  <td>
                    <span className="ci-check">✓</span> Included
                  </td>
                  <td>
                    <span className="ci-cross">✖</span> Not Included
                  </td>
                  <td>
                    <span className="ci-check">✓</span> Included
                  </td>
                </tr>
                <tr>
                  <td className="ci-table-param">Add-ons Available</td>
                  <td>
                    <span className="ci-check">✓</span> Yes
                  </td>
                  <td>
                    <span className="ci-check">✓</span> Yes
                  </td>
                  <td>
                    <span className="ci-cross">✖</span> Not Available
                  </td>
                </tr>
                <tr>
                  <td className="ci-table-param">Best For</td>
                  <td>Complete protection</td>
                  <td>Own vehicle only</td>
                  <td>Minimum legal coverage</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* BUY ONLINE WITH PARALLAX EFFECT - Updated Buy Now button */}
      <section className="ci-parallax-buy">
        <div className="ci-parallax-overlay">
          <div className="ci-container">
            <div className="ci-parallax-content">
              <div className="ci-parallax-text">
                <h2>Why buy car insurance online?</h2>
                <p className="ci-parallax-subtitle">
                  Because it's quick, simple and 100% stress-free!
                </p>
                <p className="ci-parallax-description">
                  You can easily customise and buy your car insurance policy
                  within a few minutes. No paperwork. No confusion. Just choose
                  the coverage you need and add-ons for extra protection.
                </p>
                <p className="ci-parallax-description">
                  We also have certified workshops to improve your claim
                  experience. Get digital surveys for faster claim processing,
                  pick-up and drop services for convenience.
                </p>
                <button className="ci-parallax-btn" onClick={scrollToForm}>
                  Buy Now
                </button>
              </div>
              <div className="ci-parallax-image">
                <img
                  src="./images/insurance/BuyCarInsurance.png"
                  alt="buy car insurance"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ADD-ONS CARDS WITH HOVER EFFECT */}
      <section className="ci-addons-premium">
        <div className="ci-container">
          <div className="ci-section-header-center">
            <span className="ci-section-mini-badge">Extra Protection</span>
            <h2 className="ci-section-main-title">
              Key <span className="ci-gradient-text">Add-on</span> Covers
            </h2>
            <p className="ci-section-description">
              Enhance your policy with these optional covers
            </p>
          </div>

          <div className="ci-addons-grid">
            <div className="ci-addon-premium-card">
              <div className="ci-addon-icon">🛞</div>
              <h3>Roadside assistance</h3>
              <p>
                Breakdowns come unannounced. This add-on instantly comes to your
                rescue whenever your car needs towing or arrangement of fuel.
              </p>
            </div>

            <div className="ci-addon-premium-card ci-addon-featured">
              <div className="ci-addon-icon">⚙️</div>
              <h3>Engine Protect Plus</h3>
              <p>
                Repairing your car’s heart can be expensive. This cover helps
                take care of engine repair or replacement costs.
              </p>
            </div>

            <div className="ci-addon-premium-card">
              <div className="ci-addon-icon">🔧</div>
              <h3>Consumables cover</h3>
              <p>
                Covers the cost of repairing or replacing consumables such as
                nuts, screws, engine oil, washer and similar parts.
              </p>
            </div>

            <div className="ci-addon-premium-card">
              <div className="ci-addon-icon">📉</div>
              <h3>Zero depreciation</h3>
              <p>
                Get maximum claim amount without depreciation deductions based
                on the age of the car.
              </p>
            </div>

            <div className="ci-addon-premium-card">
              <div className="ci-addon-icon">🏧</div>
              <h3>Garage cash cover</h3>
              <p>
                Provides daily allowance for alternate travel when your vehicle
                is in the garage for repair.
              </p>
            </div>

            <div className="ci-addon-premium-card">
              <div className="ci-addon-icon">💼</div>
              <h3>Personal belongings</h3>
              <p>
                Covers damage or loss of personal belongings like mobile, laptop
                and other items.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* KEY FEATURES MINIMAL TABLE */}
      <section className="ci-features-minimal">
        <div className="ci-container">
          <div className="ci-section-header-center">
            <span className="ci-section-mini-badge">Key Features</span>
            <h2 className="ci-section-main-title">
              Why Choose Our{" "}
              <span className="ci-gradient-text">Car Insurance</span>
            </h2>
          </div>

          <div className="ci-features-minimal-grid">
            <div className="ci-feature-minimal-item">
              <div className="ci-feature-minimal-icon">🛡️</div>
              <div className="ci-feature-minimal-content">
                <h4>Coverages offered</h4>
                <p>
                  Covers both damages to your own car and third-party
                  liabilities.
                </p>
              </div>
            </div>

            <div className="ci-feature-minimal-item">
              <div className="ci-feature-minimal-icon">🔧</div>
              <div className="ci-feature-minimal-content">
                <h4>Own Damage Cover</h4>
                <p>
                  <span className="ci-check">✓</span> Included in comprehensive
                  plans
                </p>
              </div>
            </div>

            <div className="ci-feature-minimal-item">
              <div className="ci-feature-minimal-icon">👥</div>
              <div className="ci-feature-minimal-content">
                <h4>Third-Party Liability</h4>
                <p>
                  <span className="ci-check">✓</span> Legal protection against
                  third-party claims
                </p>
              </div>
            </div>

            <div className="ci-feature-minimal-item">
              <div className="ci-feature-minimal-icon">➕</div>
              <div className="ci-feature-minimal-content">
                <h4>Add-ons Available</h4>
                <p>
                  <span className="ci-check">✓</span> 6+ optional covers to
                  customize your policy
                </p>
              </div>
            </div>

            <div className="ci-feature-minimal-item">
              <div className="ci-feature-minimal-icon">🎯</div>
              <div className="ci-feature-minimal-content">
                <h4>Best For</h4>
                <p>Complete protection for your car and peace of mind</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COVERAGE TABS WITH MODERN DESIGN */}
      <section className="ci-coverage-modern">
        <div className="ci-container">
          <div className="ci-section-header-center">
            <span className="ci-section-mini-badge">Coverage Details</span>
            <h2 className="ci-section-main-title">
              What's <span className="ci-gradient-text">Covered</span> & What's
              Not
            </h2>
          </div>

          <div className="ci-coverage-modern-tabs">
            <button
              className={`ci-coverage-modern-tab ${activeTab === "inclusions" ? "active" : ""}`}
              onClick={() => setActiveTab("inclusions")}
            >
              <span>✓</span> Inclusions
            </button>
            <button
              className={`ci-coverage-modern-tab ${activeTab === "exclusions" ? "active" : ""}`}
              onClick={() => setActiveTab("exclusions")}
            >
              <span>✕</span> Exclusions
            </button>
          </div>

          <div className="ci-coverage-modern-content">
            {activeTab === "inclusions" && (
              <div className="ci-coverage-modern-grid">
                <div className="ci-coverage-modern-card">
                  <ul>
                    <li>
                      <span>✓</span> Coverage for natural disasters like
                      earthquakes, floods, fires
                    </li>
                    <li>
                      <span>✓</span> Multiple add ons to choose from
                    </li>
                    <li>
                      <span>✓</span> Third-party legal liabilities cover
                    </li>
                    <li>
                      <span>✓</span> Instant claim settlement via InstaSpect
                    </li>
                    <li>
                      <span>✓</span> ₹15 lakh Personal Accident Cover
                    </li>
                    <li>
                      <span>✓</span> Cashless repairs in 7000+ network garages
                    </li>
                    <li>
                      <span>✓</span> Coverage for theft, accidents and terrorist
                      activities
                    </li>
                    <li>
                      <span>✓</span> No claim bonus up to 50%
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {activeTab === "exclusions" && (
              <div className="ci-coverage-modern-grid">
                <div className="ci-coverage-modern-card ci-coverage-modern-exclusion">
                  <ul>
                    <li>
                      <span>✕</span> Normal wear and tear of the vehicle
                    </li>
                    <li>
                      <span>✕</span> Driving without a valid license
                    </li>
                    <li>
                      <span>✕</span> Damage due to intoxicated driving
                    </li>
                    <li>
                      <span>✕</span> Mechanical or electrical breakdown
                    </li>
                    <li>
                      <span>✕</span> Using vehicle for illegal activities
                    </li>
                    <li>
                      <span>✕</span> Damage outside geographical limits
                    </li>
                    <li>
                      <span>✕</span> War or nuclear risks
                    </li>
                    <li>
                      <span>✕</span> Driving without valid insurance
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default CarInsurance;
